"use client";

import * as React from "react";
import { createScope, type Scope } from "animejs";

import { REDUCED_MOTION_QUERY } from "@libs/zapi-ui/lib/motion";

type MotionSetup<T extends HTMLElement | SVGElement> = (context: {
  root: T;
  reduced: boolean;
  scope: Scope;
}) => void | (() => void);

function useMotionScope<T extends HTMLElement | SVGElement = HTMLDivElement>(
  setup: MotionSetup<T>,
  deps: React.DependencyList = []
) {
  const root = React.useRef<T>(null);
  const scope = React.useRef<Scope | null>(null);

  React.useLayoutEffect(() => {
    const element = root.current;
    if (!element) return;

    scope.current = createScope({
      root: { current: element },
      mediaQueries: { reduced: REDUCED_MOTION_QUERY }
    }).add((self) => setup({ root: element, reduced: Boolean(self?.matches.reduced), scope: self as Scope }));

    return () => {
      scope.current?.revert();
      scope.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { root, scope };
}

export { useMotionScope, type MotionSetup };
